import axios from 'axios';
import { createApiUrlWithPrefix } from '../config/api';
import { toast } from 'react-hot-toast';

// Configure axios defaults
axios.defaults.timeout = 30000; // 30 seconds timeout
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.common['Accept'] = 'application/json';

// Add request interceptor for debugging
axios.interceptors.request.use(
  (config) => {
    // Ensure headers object exists
    if (!config.headers) {
      config.headers = {};
    }
    
    // For FormData uploads, remove Content-Type header to let axios set it with boundary
    // Check if data is FormData (browser FormData)
    if (config.data instanceof FormData) {
      // Remove Content-Type header so axios can set it automatically with boundary
      if (config.headers) {
        delete config.headers['Content-Type'];
      }
      if (config.headers?.common) {
        delete config.headers.common['Content-Type'];
      }
      console.log('📸 FormData detected - removed Content-Type header to let axios set boundary');
    }
    
    // Build full URL properly (createApiUrlWithPrefix already includes base URL)
    const fullURL = config.url?.startsWith('http') 
      ? config.url 
      : (config.baseURL || '') + (config.url || '');
    
    console.log('🌐 Web API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL || 'none',
      fullURL: fullURL,
      timeout: config.timeout,
      isFormData: config.data instanceof FormData
    });
    return config;
  },
  (error) => {
    console.error('🌐 Web API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging and auth handling
axios.interceptors.response.use(
  (response) => {
    console.log('🌐 Web API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    
    // Handle 401 (Unauthorized) - let AuthContext handle token invalidation
    // AuthContext has retry logic (3 consecutive 401s before logout)
    // We should NOT clear the token here to avoid race conditions on page refresh
    if (status === 401 && !url.includes('/auth/me')) {
      console.warn('⚠️ Authentication failed (401) on non-auth endpoint - AuthContext will handle logout if needed');
    } else if (status === 401 && url.includes('/auth/me')) {
      // For /auth/me, just log but don't clear token - let AuthContext handle it
      console.warn('⚠️ 401 on /auth/me - AuthContext will handle this');
    }
    
    // Don't log 404 errors as errors - they're often expected (e.g., no insurance, no forfait, no subscription)
    const isExpected404 = status === 404 && (
      url.includes('/insurance') || 
      url.includes('/forfait') ||
      url.includes('/status') ||
      url.includes('/subscription')
    );
    
    if (isExpected404) {
      console.log('🌐 Web API (Expected 404):', {
        url: error.config?.url,
        method: error.config?.method,
        status: status,
        note: '404 is expected if resource does not exist'
      });
    } else {
      console.error('🌐 Web API Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: status,
        message: error.message,
        code: error.code
      });
    }
    return Promise.reject(error);
  }
);

// API endpoints configuration
export const apiEndpoints = {
  users: {
    getAll: (params = {}) => axios.get(createApiUrlWithPrefix('users'), { params }),
    getByRole: (role) => axios.get(createApiUrlWithPrefix(`users?role=${role}`)),
    getById: (id) => axios.get(createApiUrlWithPrefix(`users/${id}`)),
    create: (data) => axios.post(createApiUrlWithPrefix('users'), data),
    update: (id, data) => axios.put(createApiUrlWithPrefix(`users/${id}`), data),
    delete: (id) => axios.delete(createApiUrlWithPrefix(`users/${id}`))
  },
  sessions: {
    getAll: (params = {}) => axios.get(createApiUrlWithPrefix('sessions'), { params }),
    getById: (id) => axios.get(createApiUrlWithPrefix(`sessions/${id}`)),
    create: (data) => axios.post(createApiUrlWithPrefix('sessions'), data),
    update: (id, data) => axios.put(createApiUrlWithPrefix(`sessions/${id}`), data),
    delete: (id) => axios.delete(createApiUrlWithPrefix(`sessions/${id}`)),
    getStatistics: () => axios.get(createApiUrlWithPrefix('sessions/statistics'))
  },
  payments: {
    getAll: (params = {}) => axios.get(createApiUrlWithPrefix('payments'), { params }),
    getById: (id) => axios.get(createApiUrlWithPrefix(`payments/${id}`)),
    create: (data) => axios.post(createApiUrlWithPrefix('payments'), data),
    update: (id, data) => axios.put(createApiUrlWithPrefix(`payments/${id}`), data),
    delete: (id) => axios.delete(createApiUrlWithPrefix(`payments/${id}`)),
    process: (id, data) => axios.post(createApiUrlWithPrefix(`payments/${id}/process`), data),
    getStatistics: () => axios.get(createApiUrlWithPrefix('payments/statistics'))
  },
  subscriptions: {
    getRiders: () => axios.get(createApiUrlWithPrefix('subscriptions/riders')),
    getOwners: () => axios.get(createApiUrlWithPrefix('subscriptions/owners')),
    getById: (id) => axios.get(createApiUrlWithPrefix(`subscriptions/${id}`)),
    create: (data) => axios.post(createApiUrlWithPrefix('subscriptions'), data),
    update: (id, data) => axios.put(createApiUrlWithPrefix(`subscriptions/${id}`), data),
    delete: (id) => axios.delete(createApiUrlWithPrefix(`subscriptions/${id}`))
  },
  horses: {
    getAll: (params = {}) => axios.get(createApiUrlWithPrefix('horses'), { params }),
    getById: (id) => axios.get(createApiUrlWithPrefix(`horses/${id}`)),
    create: (data) => axios.post(createApiUrlWithPrefix('horses'), data),
    update: (id, data) => axios.put(createApiUrlWithPrefix(`horses/${id}`), data),
    delete: (id) => axios.delete(createApiUrlWithPrefix(`horses/${id}`))
  },
  personnelPayments: {
    getAll: (params = {}) => axios.get(createApiUrlWithPrefix('PersonnelPayment'), { params }),
    getById: (id) => axios.get(createApiUrlWithPrefix(`PersonnelPayment/${id}`)),
    create: (data) => axios.post(createApiUrlWithPrefix('PersonnelPayment'), data),
    update: (id, data) => axios.put(createApiUrlWithPrefix(`PersonnelPayment/${id}`), data),
    delete: (id) => axios.delete(createApiUrlWithPrefix(`PersonnelPayment/${id}`)),
    getPaginated: (params = {}) => axios.get(createApiUrlWithPrefix('PersonnelPayment/paginated'), { params }),
    getByPersonnelId: (personnelId) => axios.get(createApiUrlWithPrefix(`PersonnelPayment/personnel/${personnelId}`)),
    markAsCompleted: (id) => axios.post(createApiUrlWithPrefix(`PersonnelPayment/${id}/process`)),
    cancel: (id) => axios.post(createApiUrlWithPrefix(`PersonnelPayment/${id}/cancel`)),
    getStatistics: () => axios.get(createApiUrlWithPrefix('PersonnelPayment/statistics'))
  },
  institutionalPayments: {
    getAll: (params = {}) => axios.get(createApiUrlWithPrefix('InstitutionalPayment/all'), { params }),
    getById: (id) => axios.get(createApiUrlWithPrefix(`InstitutionalPayment/${id}`)),
    create: (data) => axios.post(createApiUrlWithPrefix('InstitutionalPayment'), data),
    update: (id, data) => axios.put(createApiUrlWithPrefix(`InstitutionalPayment/${id}`), data),
    delete: (id) => axios.delete(createApiUrlWithPrefix(`InstitutionalPayment/${id}`)),
    getPaginated: (params = {}) => axios.get(createApiUrlWithPrefix('InstitutionalPayment/paginated'), { params }),
    getSummary: () => axios.get(createApiUrlWithPrefix('InstitutionalPayment/summary')),
    getStatistics: () => axios.get(createApiUrlWithPrefix('InstitutionalPayment/statistics')),
    process: (id) => axios.post(createApiUrlWithPrefix(`InstitutionalPayment/${id}/process`)),
    cancel: (id) => axios.post(createApiUrlWithPrefix(`InstitutionalPayment/${id}/cancel`))
  },
  inventory: {
    getAll: (params = {}) => axios.get(createApiUrlWithPrefix('inventory'), { params }),
    getById: (id) => axios.get(createApiUrlWithPrefix(`inventory/${id}`)),
    create: (data) => axios.post(createApiUrlWithPrefix('inventory'), data),
    update: (id, data) => axios.put(createApiUrlWithPrefix(`inventory/${id}`), data),
    delete: (id) => axios.delete(createApiUrlWithPrefix(`inventory/${id}`)),
    getPaginated: (params = {}) => axios.get(createApiUrlWithPrefix('inventory'), { params }),
    getSummary: () => axios.get(createApiUrlWithPrefix('inventory/summary')),
    getLowStock: () => axios.get(createApiUrlWithPrefix('inventory/low-stock')),
    checkSKU: (sku, excludeId = null) => axios.get(createApiUrlWithPrefix('inventory/check-sku'), { 
      params: { sku, excludeId } 
    }),
    adjustQuantity: (id, data) => axios.post(createApiUrlWithPrefix(`inventory/${id}/adjust`), data),
    // History endpoints
    addTransaction: (data) => axios.post(createApiUrlWithPrefix('inventory/history'), data),
    getItemHistory: (id, params = {}) => axios.get(createApiUrlWithPrefix(`inventory/${id}/history`), { params }),
    getAllHistory: (params = {}) => axios.get(createApiUrlWithPrefix('inventory/history'), { params })
  },
  suppliers: {
    getAll: () => axios.get(createApiUrlWithPrefix('suppliers')),
    getById: (id) => axios.get(createApiUrlWithPrefix(`suppliers/${id}`)),
    create: (data) => axios.post(createApiUrlWithPrefix('suppliers'), data),
    update: (id, data) => axios.put(createApiUrlWithPrefix(`suppliers/${id}`), data),
    delete: (id) => axios.delete(createApiUrlWithPrefix(`suppliers/${id}`))
  },
  supplierInvoices: {
    getAll: (params = {}) => axios.get(createApiUrlWithPrefix('supplier-invoices'), { params }),
    getById: (id) => axios.get(createApiUrlWithPrefix(`supplier-invoices/${id}`)),
    create: (data) => axios.post(createApiUrlWithPrefix('supplier-invoices'), data),
    addPayment: (id, data) => axios.post(createApiUrlWithPrefix(`supplier-invoices/${id}/payments`), data),
    getSummary: () => axios.get(createApiUrlWithPrefix('supplier-invoices/summary')),
    cancel: (id) => axios.delete(createApiUrlWithPrefix(`supplier-invoices/${id}`))
  },
  settings: {
    getJetonPrice: () => axios.get(createApiUrlWithPrefix('settings/jeton-price'))
  },
  riderPayments: {
    getAll: (params = {}) => axios.get(createApiUrlWithPrefix('RiderPayment/all'), { params }),
    getById: (id) => axios.get(createApiUrlWithPrefix(`RiderPayment/${id}`)),
    create: (data) => axios.post(createApiUrlWithPrefix('RiderPayment'), data),
    update: (id, data) => axios.put(createApiUrlWithPrefix(`RiderPayment/${id}`), data),
    delete: (id) => axios.delete(createApiUrlWithPrefix(`RiderPayment/${id}`)),
    getByRiderId: (riderId) => axios.get(createApiUrlWithPrefix(`RiderPayment/rider/${riderId}`)),
    cancel: (id) => axios.post(createApiUrlWithPrefix(`RiderPayment/${id}/cancel`))
  },
  ownerPayments: {
    getAll: (params = {}) => axios.get(createApiUrlWithPrefix('OwnerPayment/all'), { params }),
    getById: (id) => axios.get(createApiUrlWithPrefix(`OwnerPayment/${id}`)),
    create: (data) => axios.post(createApiUrlWithPrefix('OwnerPayment'), data),
    update: (id, data) => axios.put(createApiUrlWithPrefix(`OwnerPayment/${id}`), data),
    delete: (id) => axios.delete(createApiUrlWithPrefix(`OwnerPayment/${id}`)),
    getByOwnerId: (ownerId) => axios.get(createApiUrlWithPrefix(`OwnerPayment/owner/${ownerId}`)),
    cancel: (id) => axios.post(createApiUrlWithPrefix(`OwnerPayment/${id}/cancel`))
  },
  temporaryUserPayments: {
    getAll: (params = {}) => axios.get(createApiUrlWithPrefix('TemporaryUserPayment/all'), { params }),
    getById: (id) => axios.get(createApiUrlWithPrefix(`TemporaryUserPayment/${id}`)),
    create: (data) => axios.post(createApiUrlWithPrefix('TemporaryUserPayment'), data),
    update: (id, data) => axios.put(createApiUrlWithPrefix(`TemporaryUserPayment/${id}`), data),
    delete: (id) => axios.delete(createApiUrlWithPrefix(`TemporaryUserPayment/${id}`)),
    getByUserId: (userId) => axios.get(createApiUrlWithPrefix(`TemporaryUserPayment/user/${userId}`))
  },
  miscellaneousPayments: {
    getAll: (params = {}) => axios.get(createApiUrlWithPrefix('MiscellaneousPayments'), { params }),
    getById: (id) => axios.get(createApiUrlWithPrefix(`MiscellaneousPayments/${id}`)),
    create: (data) => axios.post(createApiUrlWithPrefix('MiscellaneousPayments'), data),
    update: (id, data) => axios.put(createApiUrlWithPrefix(`MiscellaneousPayments/${id}`), data),
    delete: (id) => axios.delete(createApiUrlWithPrefix(`MiscellaneousPayments/${id}`))
  },
  reports: {
    getInventoryReport: () => axios.get(createApiUrlWithPrefix('payments/inventory')),
    getPaymentReport: () => axios.get(createApiUrlWithPrefix('payments/payments')),
    getFinanceDashboard: (params = {}) => axios.get(createApiUrlWithPrefix('payments/finance-dashboard'), { params }),
    getFinanceTransactions: (params = {}) => axios.get(createApiUrlWithPrefix('payments/finance-transactions'), { params }),
    getRiderPayments: () => axios.get(createApiUrlWithPrefix('payments/payments/riders')),
    getOwnerPayments: () => axios.get(createApiUrlWithPrefix('payments/payments/owners')),
    getPersonnelPayments: () => axios.get(createApiUrlWithPrefix('payments/payments/personnel')),
    getTemporaryUserPayments: () => axios.get(createApiUrlWithPrefix('payments/payments/temporary')),
    getMiscellaneousPayments: () => axios.get(createApiUrlWithPrefix('payments/payments/miscellaneous'))
  },
  permissions: {
    getAll: () => axios.get(createApiUrlWithPrefix('permission/permissions')),
    getById: (id) => axios.get(createApiUrlWithPrefix(`permission/permissions/${id}`)),
    create: (data) => axios.post(createApiUrlWithPrefix('permission/permissions'), data),
    update: (id, data) => axios.put(createApiUrlWithPrefix(`permission/permissions/${id}`), data),
    delete: (id) => axios.delete(createApiUrlWithPrefix(`permission/permissions/${id}`)),
    getUserPermissions: (userId) => axios.get(createApiUrlWithPrefix(`permission/users/${userId}/permissions`)),
    grantPermission: (userId, data) => axios.post(createApiUrlWithPrefix(`permission/users/${userId}/permissions`), data),
    updateUserPermission: (userId, data) => axios.put(createApiUrlWithPrefix(`permission/users/${userId}/permissions`), data),
    bulkUpdateUserPermissions: (userId, data) => axios.post(createApiUrlWithPrefix(`permission/users/${userId}/permissions/bulk`), data),
    revokePermission: (userId, permissionId) => axios.delete(createApiUrlWithPrefix(`permission/users/${userId}/permissions/${permissionId}`)),
    checkPermission: (data) => axios.post(createApiUrlWithPrefix('permission/check'), data),
    checkMultiplePermissions: (data) => axios.post(createApiUrlWithPrefix('permission/check-multiple'), data)
  },
  seed: {
    seedPermissions: () => axios.post(createApiUrlWithPrefix('seed/permissions')),
    seedAdmin: () => axios.post(createApiUrlWithPrefix('seed/admin')),
    seedAll: () => axios.post(createApiUrlWithPrefix('seed/all')),
    getStatus: () => axios.get(createApiUrlWithPrefix('seed/status'))
  },
  debts: {
    getAll: (status) => axios.get(createApiUrlWithPrefix(`RiderPayment/debts${status ? `?status=${status}` : ''}`)),
    getByMember: (memberId) => axios.get(createApiUrlWithPrefix(`RiderPayment/member/${memberId}/debts`)),
    settle: (data) => axios.post(createApiUrlWithPrefix('RiderPayment/debts/settle'), data)
  }
};

// Error handling utility
export const handleApiError = (error, defaultMessage = 'An error occurred') => {
  console.error('API Error:', error);
  
  if (error.response) {
    // Server responded with error status
    const message = error.response.data?.message || error.response.data?.error || defaultMessage;
    toast.error(message);
  } else if (error.request) {
    // Request was made but no response received
    toast.error('Network error - please check your connection');
  } else {
    // Something else happened
    toast.error(defaultMessage);
  }
};

export default apiEndpoints;
